/**
 * Every error the API can put on the wire, as a stable machine code.
 * The API never sends prose (docs/008 invariant S8); the client renders
 * `t(\`errors:${toErrorKey(code)}\`)`.
 *
 * `pnpm i18n:check` fails when a code declared here has no matching `en` key in
 * `packages/i18n/locales/en/errors.json`, so adding a code here means adding a
 * translation in the same change.
 */
export const ERROR_CODES = [
    'INTERNAL_ERROR',
    'SERVICE_UNAVAILABLE',
    'VALIDATION_FAILED',
    'NOT_FOUND',
    'UNAUTHORIZED',
    'FORBIDDEN',
    'TENANT_NOT_FOUND',
    // 429, emitted only by the rate limiter (apps/api/src/lib/route-declaration.ts).
    // It exists so a throttled caller is told THEIR request was refused; without a
    // code of its own the limiter's reply is unparseable to the SDK and degrades to
    // `INTERNAL_ERROR`, which blames the server for a control working correctly.
    'RATE_LIMITED',
    // Authentication. `INVALID_CREDENTIALS` is deliberately returned for an unknown email
    // AND for a wrong password: distinguishing them turns the login form into a user
    // enumeration oracle.
    'INVALID_CREDENTIALS',
    'ACCOUNT_SUSPENDED',
    'TOKEN_EXPIRED',
    'TOKEN_INVALID',
    'NO_TENANT_MEMBERSHIP',
    'TENANT_REQUIRED',
    'TENANT_SUSPENDED',
    // Location tree conflicts.
    'LOCATION_HAS_CHILDREN',
    'LOCATION_PARENT_CYCLE',
    // Nested location creation/re-parenting is disabled by product decision (2026-09-21) —
    // `directory/service.ts`'s `createLocation`/`updateLocation`. Locations are flat; this is
    // the code a caller gets for naming a non-null `parentLocationId` in a create, or changing
    // an existing location's parent in an update.
    'LOCATION_NESTING_DISABLED',
    // A location still referenced by a device, an unconsumed pairing code, a visit
    // or an invite (S14 Phase 1). Distinct from `LOCATION_HAS_CHILDREN`: that one
    // names a cause the admin can fix by moving rows in the location tree, while
    // this one usually means waiting for a visitor to check out or retiring a
    // kiosk — there is nothing to reparent.
    'LOCATION_IN_USE',
    // Each of the four below names exactly ONE still-active referrer that blocks
    // deleting a location (S17 Phase 1), so the admin is told which thing to fix
    // rather than being handed the generic answer. They replace `LOCATION_IN_USE`
    // as the code a user normally sees; `LOCATION_IN_USE` is KEPT on purpose as
    // the residual/race code, because the counts and the delete run in one
    // transaction at READ COMMITTED and a reference created between them still
    // raises a raw `P2003`. `LOCATION_HAS_ACTIVE_VISITS` and
    // `LOCATION_HAS_ACTIVE_INVITES` have no throw site until S17 Phase 2, when
    // history learns to detach — until then those references still surface as
    // `LOCATION_IN_USE`.
    'LOCATION_HAS_ACTIVE_DEVICES',
    'LOCATION_HAS_PENDING_PAIRING_CODE',
    'LOCATION_HAS_ACTIVE_VISITS',
    'LOCATION_HAS_ACTIVE_INVITES',
    // Department tree conflicts. The first two mirror the Location pair above; the
    // third has no Location equivalent because only a department holds people.
    'DEPARTMENT_HAS_CHILDREN',
    'DEPARTMENT_PARENT_CYCLE',
    'DEPARTMENT_HAS_PEOPLE',
    // Person reporting-line conflicts. Assigning a manager who is the person
    // themselves, or anywhere below them in the chain (S3 Phase 4). There is
    // deliberately no `PERSON_HAS_REPORTS`: `Person.managerId` is `onDelete:
    // SetNull`, so deleting a manager clears their reports' manager rather than
    // being refused.
    'MANAGER_CYCLE',
    // Person location/assistant conflicts (S30 Phase 1). A location outside the tenant, or
    // outside the caller's permitted scope, deliberately reuses `NOT_FOUND` rather than a
    // new code (S1 — never confirm existence to an out-of-scope caller); these two are
    // NOT existence checks, so they get real codes.
    // A person's requested secondary location duplicates their primary (the one already
    // stored, or the one in the same request). Refused rather than silently deduplicated:
    // the caller asked for both roles on one site, and exactly one of those two inputs is
    // wrong. Thrown by `apps/api`'s person service (S30-TASK-004).
    'SECONDARY_DUPLICATES_PRIMARY',
    // A person named as their own assistant (S30 Phase 1) — the assistant relation's
    // equivalent of `MANAGER_CYCLE`'s self-assignment case, but with no chain to walk: a
    // direct self-reference is the only possible cycle, since the relation carries no
    // transitive edge the way `managerId` does. Thrown by `apps/api`'s person service
    // (S30-TASK-004).
    'ASSISTANT_SELF',
    // 409. Product decision 2026-09-25: an `EMPLOYEE` person's email must be unique among a
    // tenant's employee contacts, matched trimmed and case-insensitively. Thrown by
    // `apps/api/src/modules/platform/directory/service.ts`'s `createPerson`/`updatePerson`
    // (`assertEmployeeEmailAvailable`) — never backed by a database unique constraint, since a
    // partial, case-insensitive unique index is not expressible in Prisma's schema DSL and
    // existing dev data already has duplicates (see `Person.email`'s doc comment in
    // `packages/db/prisma/schema.prisma`). `null` emails never collide, and a non-`EMPLOYEE`
    // person's email is never constrained by this rule.
    'EMPLOYEE_EMAIL_TAKEN',
    // The visitor-log CSV export refuses past its row cap instead of truncating
    // (S10 Phase 2). A silently half-complete evacuation or security record is
    // worse than no file at all, and the caller can always narrow the date range.
    // 400, not 413: 413 describes an oversized REQUEST body, and this request is a
    // query string. The row cap itself lives in the visitor service, not here —
    // the wire needs the code, not the number.
    'VISIT_EXPORT_TOO_LARGE',
    // 409. A host reply (or later host action) on a visit already checked out. S48-lh45.
    'VISIT_NOT_OPEN',
    // 409. The host already gave the other answer to this walk-in. S48-lh45.
    'WALK_IN_APPROVAL_ALREADY_DECIDED',
    // 409. The walk-in waited longer than the approval window. S48-lh45.
    'WALK_IN_APPROVAL_EXPIRED',
    // The evacuation roster CSV export refuses past its row cap instead of truncating
    // (S14 Phase 3) — same "refuse rather than half-complete" reasoning as
    // VISIT_EXPORT_TOO_LARGE above, but a SECOND code and not a reuse of it: the two
    // refusals have different remedies, and the user-facing string says so.
    // `visitExportTooLarge` tells the caller to narrow the date range or filters; the
    // roster export has no date range and no filters at all — its only dimension is
    // `locationId` — so that remedy would be false. (Amends the milestone changelog's
    // decision (7), which counted `LOCATION_IN_USE` as the milestone's only new code.)
    'EVACUATION_EXPORT_TOO_LARGE',
    // More audit events match than one Activity CSV export carries (AUDIT_EXPORT_MAX_ROWS). Its
    // own code: the remedy is "narrow the date range", not a visit-log or evacuation remedy.
    'AUDIT_EXPORT_TOO_LARGE',
    // The uploaded employee-import file could not be parsed as the declared content type —
    // corrupt bytes, a non-XLSX file with the XLSX content type, or similar (S30 Phase 3).
    // A 400, thrown by `previewEmployeeImport` (and, from Phase 4, the commit route) BEFORE
    // any row-level validation runs — this is a transport-level failure, never a row in the
    // EmployeeImportReportDTO's own `errors` array (those are a separate, closed union in
    // packages/contracts/src/employee-import.ts and never reach ERROR_CODES).
    'FILE_UNREADABLE',
    // An employee-import file's data-row count exceeds EMPLOYEE_IMPORT_MAX_ROWS (S30 Phase 3).
    // Refused rather than truncated — the same "no half-complete bulk operation"
    // reasoning as VISIT_EXPORT_TOO_LARGE/EVACUATION_EXPORT_TOO_LARGE above, applied to an
    // upload instead of an export.
    'IMPORT_TOO_MANY_ROWS',
    // Cancelling an invite that has already been redeemed (S11 Phase 1). Refused rather
    // than silently ignored: the visitor is already in the building and the resulting
    // VisitorVisit is a security record, so "cancel" has no honest meaning. Cancelling an
    // already-CANCELLED invite is NOT this error — it is idempotent and returns the row,
    // the same call check-out already makes (`service.ts#checkOutVisit`).
    'INVITE_NOT_CANCELLABLE',
    // A device-presented arrival code that is malformed, unknown, expired, cancelled or
    // already redeemed — ONE code for all five causes (S11 Phase 2, INV-11, milestone
    // Constraint 7). Distinguishing them would let a caller narrow a guess by watching which
    // rejection they got back; docs/008 S8 requires exactly this kind of oracle to be closed.
    'INVITE_CODE_INVALID',
    // 404. S41. A Visit Pass token that is malformed or unknown — ONE code for both, the
    // `INVITE_CODE_INVALID` rule (docs/008 S8): no oracle. A KNOWN pass that is cancelled or
    // expired is `VISIT_PASS_GONE` instead, carrying only `{ status }` — the holder already
    // proved possession of the link, so telling them it is dead leaks nothing.
    'VISIT_PASS_INVALID',
    // 410. S41. See `VISIT_PASS_INVALID`. `details` carries only the pass status (cancelled or
    // expired) — see `visitPassGoneDetailsSchema`.
    'VISIT_PASS_GONE',
    // 409. S41. A write to a pass whose invite was already redeemed — the pass is read-only
    // ("You're checked in").
    'VISIT_PASS_READ_ONLY',
    // 409. S41. Resending an invite that is redeemed, cancelled or expired — there is no live
    // visit to send a pass for.
    'INVITE_NOT_RESENDABLE',
    // 402, not 403 (docs/004:52-60, docs/006:20). The tenant's plan does not include the
    // feature; the CALLER IS ALLOWED to use it and would be, one plan up. A 403 here hides the
    // upgrade path and silently costs revenue, which is the whole reason this code exists as
    // its own status. `details` carries `{ featureKey, currentPlanCode }` so a client renders a
    // targeted prompt without a second round trip — read it with
    // `parsePlanUpgradeRequiredDetails` (./entitlement.js), never by indexing.
    'PLAN_UPGRADE_REQUIRED',
    // 402 as well, and deliberately a SECOND code rather than a reuse of the one above — the
    // same "different remedy, different code" reasoning `EVACUATION_EXPORT_TOO_LARGE` records
    // against reusing `VISIT_EXPORT_TOO_LARGE`. The feature IS on the tenant's plan; they have
    // reached its numeric cap. The remedy is "upgrade for a higher cap or remove one", never
    // "buy this feature", and the user-facing string says so. `details` carries
    // `{ featureKey, currentPlanCode, limit, current }`.
    'PLAN_LIMIT_REACHED',
    // 402, and a THIRD payment-required code rather than a reuse of either above — the same
    // "different remedy, different code" rule `EVACUATION_EXPORT_TOO_LARGE` records against
    // reusing `VISIT_EXPORT_TOO_LARGE`. S26 Phase 4.
    //
    // The tenant's trial has ended and they have chosen nothing, so they hold no contributing
    // subscription at all. This is NOT "your plan does not include this feature"
    // (`PLAN_UPGRADE_REQUIRED`) and NOT "you have hit a cap" (`PLAN_LIMIT_REACHED`): there is no
    // plan to speak of. The remedy is to pick one, and FREE IS ONE OF THE CHOICES — a UI that
    // rendered the upgrade prompt here would show an upsell to someone whose correct next action
    // costs nothing.
    //
    // Every route except `/auth/*`, `/billing/*` and `/auth/me` answers this for such a tenant
    // (S26-TASK-016); the SPA turns it into a full-page plan-selection route, so the user signs
    // in successfully and can do nothing else until they choose.
    'PLAN_SELECTION_REQUIRED',
    // 503. The ONLY surface that reports a translation failure to a caller: the
    // explicit `POST /api/web/fields/:id/translations`. Creating or editing a field
    // never emits this — there, an unreachable translator degrades to saving the
    // English label alone, because failing an admin's write over a machine
    // convenience would make the convenience a liability. Regenerating has nothing
    // else it could have meant, so it says so.
    'TRANSLATION_UNAVAILABLE',
    // 500. The `cognito` identity adapter's `issueAccessToken` (S22-TASK-004) reached its
    // provider-session check with no session, or one of the wrong shape — meaning the
    // credential check was skipped and minting a token would be authentication by omission.
    // A caller never triggers this through normal use; it names a broken call site, not a
    // credential problem, so it is distinct from `INVALID_CREDENTIALS`.
    'IDENTITY_PROVIDER_ERROR',
    // 400. S26 Phase 1. A password-reset code that does not redeem: wrong, expired, already
    // consumed, or past its attempt cap. ONE code for all four, for the same reason
    // `INVITE_CODE_INVALID` is one code for five causes (docs/008 S8) — telling them apart lets a
    // caller narrow a guess by watching which rejection comes back. The `reason` on
    // `AUTH_PASSWORD_RESET_REJECTED` is where an operator sees which it was.
    'INVALID_RESET_CODE',
    // 400. S26 Phase 1. The submitted new password does not satisfy the identity provider's
    // password policy. Distinct from `VALIDATION_FAILED` on purpose: our own `passwordSchema`
    // mirrors the pool policy, so reaching this means the two have DRIFTED — the client-side
    // rule accepted something the provider then refused. The user-facing string states the rule;
    // the operator-facing signal is that this code should be rare, and a spike in it means
    // `passwordSchema` and `identity-stack.ts` need reconciling.
    'PASSWORD_POLICY',
    // 400. S26 Phase 2/3. A verification code — email or SMS — that does not redeem: wrong,
    // expired, already consumed, or past its attempt cap. ONE code for all four, and one code
    // ACROSS BOTH CHANNELS, for the reason `INVITE_CODE_INVALID` records: a rejection that names
    // its cause lets a caller narrow a guess, and a rejection that names its CHANNEL additionally
    // confirms which contact details we hold. Told apart only in the log `reason`.
    'VERIFICATION_CODE_INVALID',
    // 409. S26 Phase 3/5. Signup, or a member invite, was given an email address that already has
    // a `User` row. This one DELIBERATELY BREAKS the "never confirm an account exists" posture that
    // `INVALID_CREDENTIALS` and forgot-password hold, and the trade is made knowingly: a signup form
    // that silently succeeds without creating an account is unusable, and the address is one the
    // person submitting it has in front of them. Milestone Constraint 3 — one email, one tenant —
    // is the boundary check that raises it; the model supports many memberships per user and
    // reversing this is one deleted check.
    'EMAIL_ALREADY_REGISTERED',
    // 400. Issue #150. The bot-protection check on a public form failed: the token was missing from
    // the captcha vendor's point of view, rejected, expired, already used, or the vendor was
    // unreachable (signup fails CLOSED). One code for all of them on purpose — the reason is in the
    // server log, never on the wire. The form re-arms its widget and asks the person to retry.
    'CAPTCHA_FAILED',
    // 409. S26 Phase 3. The requested tenant slug is taken. A FIELD-LEVEL answer, not a 500: today
    // a duplicate slug escapes `createTenant` as a raw Prisma unique violation, which the signup
    // form cannot render against the field the user must actually change. Reserved slugs are a
    // different failure — `tenantSlugSchema` rejects those as `VALIDATION_FAILED` before any
    // database round trip.
    'TENANT_SLUG_TAKEN',
    // 400. S26 Phase 2. The phone number's country is not on the SMS allowlist. SMS pumping fraud
    // bills real money to whoever leaves a send endpoint open to arbitrary destinations, so the
    // allowlist is a spend control, not a product rule. Named rather than folded into
    // `VALIDATION_FAILED` so the UI can say something true — the number is well-formed, we just do
    // not send there — and so the rejection is countable on its own in the logs.
    'PHONE_COUNTRY_NOT_ALLOWED',
    // 400. S35 Phase 2 (PRD-009 BDG-03b). A badge layout whose slot stack or sidebar cannot
    // fit inside its orientation's canvas — checked with `badgeLayoutFits()`
    // (packages/contracts/src/badge-layout.ts) BEFORE the write transaction opens, so a
    // rejected save never touches the database. `details` carries the four dot measurements
    // (`slotStackHeightDots`, `availableStackHeightDots`, `sidebarExtentDots`,
    // `availableSidebarExtentDots`) rather than prose — the client renders its own message
    // from the code, same as every other error here.
    'BADGE_LAYOUT_OVERFLOW',
    // 400. S35 Phase 3 (PRD-009 PRT-01). The chosen medium needs two colour planes and the chosen
    // model cannot lay down a second one — `badgePrinterSupportsMedia()`
    // (packages/contracts/src/badge-printer.ts) says no. NOT `PLAN_UPGRADE_REQUIRED` and NOT a
    // permission error: nothing about the caller's plan or rights is wrong, the two things they
    // picked are simply incompatible, and the remedy is "change the medium or change the model".
    // `details` carries `{ model, media }` so the client renders which pair it rejected without a
    // second round trip.
    'BADGE_PRINTER_MEDIA_UNSUPPORTED',
    // 409. S35 Phase 3. Two printers at one location sharing a name are indistinguishable in the
    // picker an operator uses to choose one, which is the only place the name is ever read. The
    // database enforces it with `@@unique([tenantId, locationId, name])` (S35-TASK-025), so this
    // code is the service's translation of that constraint, never a best-effort pre-check.
    'BADGE_PRINTER_NAME_TAKEN',
    // 400. S35 Phase 3 (PRT-01b). A `Device` may only be mapped to a printer registered at ITS OWN
    // location — a lobby kiosk pointing at a printer in another building prints a badge nobody can
    // collect. Prisma cannot express a constraint that spans two rows' `locationId`, so the service
    // enforces it and answers with this code.
    'BADGE_PRINTER_LOCATION_MISMATCH',
    // 409. S35 Phase 6 (PRD-009 PRT-06/PRT-08). A reprint was asked for at a location with no
    // ACTIVE `visitor-kiosk` device to execute it. The request is well-formed and the caller is
    // entitled — the location simply has nothing that can put a label on paper right now. The
    // remedy is the documented browser fallback (PRT-12), which is why the console points at it
    // here.
    'BADGE_REPRINT_NO_KIOSK',
    // 409. S38 Phase 5. `POST /billing/apps` named an application the tenant already holds a
    // CONTRIBUTING `PLAN`-kind subscription for. The remedy is the portal/checkout flow that
    // manages an existing subscription, never a second add — see `CHECKOUT_REQUIRED` below for
    // the sibling case (the row exists but does not contribute).
    'APP_ALREADY_SUBSCRIBED',
    // 409. S38 Phase 5. `POST /billing/apps` named an application whose trial is already used up
    // (a lapsed or cancelled row exists) or whose plan carries no trial at all. The remedy is the
    // existing hosted checkout (ADR-0027), not this route — a tenant gets exactly one provider-less
    // trial per application, ever.
    'CHECKOUT_REQUIRED',
    // 409. S38 Phase 5. An operator's `PUT /billing/tenants/:tenantId/apps/:app/trial` named an
    // application whose current row is provider-backed (a real, paying subscription). A paying
    // subscription is changed at the provider, never under it — the same principle
    // `decidePlanSlot`'s `refuse` branch enforces for every other non-webhook plan writer.
    'APP_PROVIDER_BACKED',
    // 400. S38 Phase 5. An operator's `PUT /billing/tenants/:tenantId/apps/:app/trial` named a
    // plan whose `trialDays` is null or zero — there is no trial length to grant.
    'PLAN_HAS_NO_TRIAL',
    // 400. S38 Phase 5 (S38-TASK-067). An operator's `PUT /billing/tenants/:tenantId/apps/:app/plan`
    // named a self-serve plan of a LAUNCHED app. Only a sales-led plan (`Plan.selfServe = false`,
    // e.g. Visitor Enterprise) or a plan of an app not yet launched is assigned by an operator; a
    // launched app's self-serve tier is bought through checkout.
    'PLAN_IS_SELF_SERVE',
    // 409. S38 Phase 5 (S38-TASK-054). `POST /billing/checkout-session` named a plan for an
    // application the tenant does not yet pay for, while the tenant already has a provider
    // subscription billing a DIFFERENT application. A second app is a line added to the existing
    // subscription (`POST /billing/apps`, ADR-0027), never a second checkout / second provider
    // subscription.
    'ADD_APP_REQUIRED',
    // 404. S38 Phase 5 (S38-TASK-055). `DELETE /billing/apps/:app` named an application the tenant
    // holds no CONTRIBUTING row for (never held, or already lapsed/cancelled).
    'APP_NOT_SUBSCRIBED',
    // 409. S38 Phase 5 (S38-TASK-055). `DELETE /billing/apps/:app` named a provider-backed line
    // with live siblings whose `SubscriptionItem.externalItemRef` the next webhook has not yet
    // backfilled — there is nothing yet to remove at the provider, so the cancel is refused rather
    // than guessed at.
    'LINE_NOT_YET_KNOWN',
    // 409. S38 Phase 5 (S38-TASK-063). A checkout asked for an interval the plan has no active
    // price for, or `POST /billing/apps` would add a line at an interval the new plan is not sold
    // at: one provider subscription bills at one interval (ADR-0027), so an annual subscription
    // only takes plans with a YEAR price. Never silently billed at another interval.
    'INTERVAL_UNAVAILABLE',
    // 409. Handoff 0010 §5 (the stale-preview guard). A confirming request to a money-changing
    // route carried `expectedDueNow`/`expectedRecurring` that no longer match the server's freshly
    // computed preview — a price, a count or the tenant's plan moved between preview and confirm.
    // Nothing was changed. `details.preview` carries the fresh preview (`priceChangedDetailsSchema`)
    // so the UI re-shows the new numbers; a customer is never charged an amount they did not see.
    'PRICE_CHANGED',
    // 402. Handoff 0010 Phase D1. An immediate, charged-now change (an upgrade, a quantity
    // increase, an added application) was sent with `payment_behavior: pending_if_incomplete`, and
    // the provider could not collect the charge — so the change was NOT applied (it waits at the
    // provider until paid, then expires). The customer updates their payment method and retries.
    'PAYMENT_FAILED',
    // 402. Handoff 0012 §5. As `PAYMENT_FAILED`, but the bank asked the customer to authenticate the
    // payment (3-D Secure) rather than refusing it. The change waits at the provider; the customer
    // completes the payment on the provider's hosted invoice page (`details: { hostedInvoiceUrl }`,
    // `paymentActionRequiredDetailsSchema`) and the change applies when it lands.
    'PAYMENT_ACTION_REQUIRED',
    // 404. Handoff 0012 §5. `POST /billing/payment/retry` with no parked charged change to pay.
    'NO_PENDING_PAYMENT',
    // 409. Handoff 0012 §5. `POST /billing/payment/retry` whose `expectedAmountDue` differs from the
    // parked change's open amount — the change on screen is not the one waiting. Nothing was paid.
    'PENDING_PAYMENT_MISMATCH',
    // 409. Handoff 0012 §5 (option B). A change that charges money now while another charged change
    // is waiting for its bank payment to clear — one at a time. Free changes are still allowed, and
    // the pending one can be cancelled. Nothing was changed.
    'CHARGED_CHANGE_PENDING',
    // 409. Handoff 0012 §5. Cancelling a pending charged change whose bank payment is already
    // processing at the provider — it cannot be stopped; it applies or fails when the bank answers.
    'CHARGED_CHANGE_PROCESSING',
    // Handoff 0010 §1–§2 — `POST/DELETE /billing/subscription/change`.
    //
    // 409. The plan change names the plan and interval the application is already billed at —
    // there is nothing to change.
    'PLAN_UNCHANGED',
    // 409. A `kind: 'plan'` change named the free Visitor tier. Down to Free ends the paid plan at
    // the period end (`kind: 'free'`); it is never a re-price to a zero-priced line.
    'FREE_CHANGE_REQUIRED',
    // 409. Handoff 0010 Phase D2. `POST /billing/subscription/free` from a tenant that pays through
    // the billing provider. That route is the provider-less exit (the plan-selection wall, a lapsed
    // trial) and changes nothing billed; a paying tenant goes to Free through
    // `POST /billing/subscription/change` `{ kind: 'free' }`, which is previewed and confirmed and
    // ends the paid plan at the period end. Nothing was changed.
    'PLAN_CHANGE_REQUIRED',
    // 404. `DELETE /billing/subscription/change` — the tenant has no pending plan change to cancel.
    'NO_PENDING_CHANGE',
    // 409. `POST /billing/apps/:app/resume` — the application is not set to end at its period end,
    // so there is nothing to un-cancel.
    'APP_NOT_CANCELLING',
    // Handoff 0011 — per-location app subscriptions (`/billing/apps/:appId`).
    //
    // 409. `DELETE /locations/:id` for a location still SUBSCRIBED (or ENDING) to an app. Location
    // screens never change the bill: the UI says "unsubscribe it on the Billing page first" and
    // links there. `details: { apps }` (`locationSubscribedDetailsSchema`). Nothing was changed.
    'LOCATION_SUBSCRIBED',
    // 409. A staged subscription change does not fit a location's current state — subscribing one
    // already subscribed, unsubscribing one that is not, undoing one that is not ENDING.
    // `details: { locationId, action, state }`. Nothing was changed; the page refreshes.
    'LOCATION_SUBSCRIPTION_CONFLICT',
    // 409. A plan change or checkout whose location limit is below the number of SUBSCRIBED
    // locations. `details: { app, limit, subscribedCount }`; the UI links to the app billing page
    // to unsubscribe first. Replaces the keep-list step. Nothing was changed.
    'SUBSCRIBED_LOCATIONS_OVER_LIMIT',
    // 404. `/billing/apps/:appId/changes*` for an application that is not subscribed per location.
    'APP_NOT_LOCATION_SCOPED',
    // 409. Visitor activity (check-in, invite, kiosk pairing, badge print, …) at a location that is
    // not subscribed to Visitor (neither ACTIVE nor ENDING with `endsAt` ahead). DEVICE-FACING too
    // (the kiosk contract, `/api/device/v1`): the kiosk shows "Visitor isn't enabled at this
    // location"; an admin subscribes the location on `/billing/apps/visitor`. Nothing was changed.
    'LOCATION_NOT_SUBSCRIBED',
    // 409. A write to a location's kiosk branding or kiosk settings override while the organization
    // has turned `allowLocationOverride` off for that group. Nothing was changed; the location keeps
    // using the organization values. `details: { group }` (`branding` | `kiosk_settings`).
    'LOCATION_OVERRIDE_NOT_ALLOWED',
    // S42-m4dr — member role sets (`PUT /members/:id/roles`, `POST /members/invite`). Each refusal
    // has its own code so the role editor can say something true about the row it refused;
    // `details` carries `{ index }` (the submitted row) or `{ roleKey, scopeType, locationId,
    // change }` (the grant the caller may not make or remove).
    //
    // 403. Changing your own roles. Refused outright (Decision 3): an administrator trimming their
    // own access is how a tenant locks itself out.
    'ROLE_SELF_EDIT',
    // 403. Granting or removing a grant the caller does not itself hold at that scope (Decision 2):
    // an admin granting or removing `tenant.owner`, a location-scoped admin minting a tenant-wide one.
    'ROLE_GRANT_FORBIDDEN',
    // 409. The change would leave the tenant with no active `tenant.owner` (Decision 4). A state
    // conflict, not a permission problem: the same request succeeds once another owner exists.
    'LAST_OWNER',
    // 400. A grant at a scope its role does not allow (`tenant.admin` at a location), a LOCATION
    // grant with no location, a TENANT grant naming one, or a role that does not exist.
    'ROLE_SCOPE_INVALID',
    // 400. The same `(role, scope)` twice in one set. A redundant grant — the same role tenant-wide
    // AND at a location — is not a duplicate and is allowed.
    'ROLE_DUPLICATE',
    // 400. A LOCATION grant naming a location that does not exist in the caller's tenant.
    'ROLE_LOCATION_UNKNOWN',
    // S42-m4dr Phase 2 — pending MEMBER invites (`POST /members/:id/invite/resend|revoke`). Not the
    // S41 visitor-invite `INVITE_NOT_RESENDABLE` above: these are about a colleague's account.
    //
    // 409. The membership is not a pending invite any more — accepted, revoked or suspended —
    // so there is no link to resend or revoke.
    'INVITE_NOT_PENDING',
    // 429. The last invite or resend to this member went out under a minute ago. Derived from the
    // outstanding token's `createdAt`; `details.retryAfterSeconds` says how long to wait.
    'INVITE_RESEND_TOO_SOON',
    // S46-f22b Phase 1 — operator partners console and the customer's partner card (adr/0035).
    //
    // 409. Another partner already has this exact name (`Partner.name` is unique).
    'PARTNER_NAME_TAKEN',
    // 409. The partner still has linked customers; unlink them before deleting it.
    'PARTNER_HAS_CUSTOMERS',
    // 409. The tenant is already linked to a partner (a tenant has at most one).
    'TENANT_ALREADY_HAS_PARTNER',
    // 409. The partner is SUSPENDED or TERMINATED, so no customer may be linked to it.
    'PARTNER_NOT_ACTIVE',
    // 403. Valid credentials, but the account is not ACTIVE partner staff (no PartnerMembership, or one
    // that is not ACTIVE) — the partner-portal twin of NO_TENANT_MEMBERSHIP.
    'NO_PARTNER_MEMBERSHIP',
    // 403. Valid credentials and an ACTIVE membership, but the partner is SUSPENDED or TERMINATED.
    'PARTNER_SUSPENDED',
    // 403. The partner portal is not open to this caller (the API's PARTNER_PORTAL_ACCESS setting is
    // `closed`, or `superadmin` and the caller is not a platform SUPERADMIN). Shown as "Access denied".
    // One code for every refusal and no details, so it never reveals whether a credential was valid.
    'PARTNER_PORTAL_CLOSED',
    // 403. A Control Tower sign-in for a user who is not a platform SUPERADMIN (S52-jecr). No code is issued.
    'CONTROL_TOWER_ACCESS_DENIED',
    // 403. The customer has turned off setup access for its partner, so no setup session may be opened.
    'PARTNER_SETUP_ACCESS_REVOKED',
    // 400. A setup handoff code is unknown, expired, already used, or its session/link is no longer
    // live — one code for every case so the exchange is not an oracle (docs/008 S8).
    'SETUP_HANDOFF_INVALID',
    // 409. A partner tried to name (or re-send to, or replace) a customer's first owner after
    // somebody at that customer had already joined — from then on the customer invites its own users.
    'CUSTOMER_OWNER_ALREADY_ACTIVE',
    // 409. This tenant's billing is paid by its partner (wholesale): the tenant's own members cannot
    // preview, buy, change or cancel anything, nor read an amount. Carries no details.
    'BILLING_MANAGED_BY_PARTNER',
    // 409. A partner-portal billing action that only a WHOLESALE partner has (the hosted billing
    // portal): a REFERRAL partner is not billed by us for its customers.
    'PARTNER_NOT_WHOLESALE',
    // 409. Marking a partner's commission paid through the current (still accruing) month, or a later one.
    'COMMISSION_PERIOD_OPEN',
    // 409. Nothing is owed to the partner in that currency through that period (zero or a negative balance).
    'COMMISSION_NOTHING_TO_PAY',
    // 409. The amount owed through that period is no longer the one the operator confirmed. Nothing was paid.
    'COMMISSION_AMOUNT_CHANGED',
    // PRD-010 chat integrations (Settings → Integrations).
    //
    // 400. The OAuth `state` relayed to `POST /tenant/integrations/:provider/complete` failed its
    // HMAC, expired, was minted for another tenant or user, or its browser nonce cookie did not
    // match. Deliberately one code with no detail — which check failed is a log line, not a hint.
    'INTEGRATION_STATE_INVALID',
    // 400. The vendor refused to exchange the authorization code (expired, reused, wrong app).
    'INTEGRATION_INSTALL_FAILED',
    // 400. Microsoft Teams (PRD-010 slice 3): the admin who completed consent signs in with an
    // email whose domain is not a verified domain of the Microsoft 365 organization they connected.
    'INTEGRATION_TENANT_UNVERIFIED',
    // 409. The provider has no adapter in this deployment (`CHAT_SLACK_ADAPTER=disabled` or
    // `CHAT_TEAMS_ADAPTER=disabled`).
    'INTEGRATION_PROVIDER_UNAVAILABLE',
    // 400. A location filter named a location that is not one of the caller's tenant's.
    'INTEGRATION_LOCATION_UNKNOWN',
    // 422. The test message found no workspace member with the caller's email address.
    'CHAT_USER_NOT_FOUND',
    // PRD-010 CHAT-09 (shared-channel copies).
    //
    // 409. The integration was installed before channel posting existed and lacks the scopes /
    // permissions it needs (`channels:read` + `chat:write.public` on Slack). Reconnect fixes it.
    'CHAT_CHANNEL_SCOPES_MISSING',
    // 409. The integration is not ACTIVE (revoked or in error) — its channels cannot be listed.
    'INTEGRATION_NOT_ACTIVE',
    // 400. The picked channel is not one the bot can post to in the connected workspace.
    'CHAT_CHANNEL_UNKNOWN',
    // 409. That channel already has a route on this integration.
    'CHAT_CHANNEL_ROUTE_EXISTS',
    // 409. The integration already has `CHAT_CHANNEL_ROUTES_MAX` channel routes.
    'CHAT_CHANNEL_ROUTES_LIMIT',
    // 409. S47-rsq5 Phase 3: the person cannot be given Directory access from a profile right now; `details` = { status, reason } from GET …/access.
    'DIRECTORY_ACCESS_UNAVAILABLE',
    // 409. S48-lh45 Phase 4b: every occurrence of the invite is cancelled, so there is no calendar file to give.
    'INVITE_CANCELLED',
    // S49-3u50 external directory sync.
    // 409. An action needs an ACTIVE directory-sync connection and there is none.
    'DIRECTORY_SYNC_NOT_CONNECTED',
    // 409. Connecting a provider while a different provider is the tenant's ACTIVE source.
    'DIRECTORY_SYNC_ALREADY_CONNECTED',
    // 400. The OAuth `state` failed verification (HMAC, expiry, tenant, user, provider, nonce).
    'DIRECTORY_SYNC_STATE_INVALID',
    // 400. The provider refused the authorization code.
    'DIRECTORY_SYNC_CONNECT_FAILED',
    // 409. Preview/apply with no scope item.
    'DIRECTORY_SYNC_SCOPE_REQUIRED',
    // 409. Another run holds the tenant's lease.
    'DIRECTORY_SYNC_IN_PROGRESS',
    // 409. Apply named a run that is not PREVIEWED or is older than the TTL.
    'DIRECTORY_SYNC_PREVIEW_STALE',
    // 400. A scope item maps to a location not in the tenant.
    'DIRECTORY_SYNC_LOCATION_UNKNOWN',
    // 409. The provider has no adapter in this deployment.
    'DIRECTORY_SOURCE_UNAVAILABLE',
    // 502. The provider failed mid-run (outage, throttling, an unreadable answer).
    'DIRECTORY_SOURCE_FAILED',
    // 409. Sync now while the admin has paused automatic sync.
    'DIRECTORY_SYNC_PAUSED',
    // 409. Sync now before the first preview has been applied (owner decision: preview before the first apply).
    'DIRECTORY_SYNC_PREVIEW_REQUIRED',
    // 409. PATCH /people/:id changed a field the connected directory owns; details = { fields: DirectorySyncedPersonField[] }.
    'PERSON_FIELD_SYNCED',
    // 409. Sync now while a held run awaits the admin's approve-or-discard.
    'DIRECTORY_SYNC_HELD_FOR_APPROVAL',
    // 409. Approve/discard named a run that is not HELD_FOR_APPROVAL.
    'DIRECTORY_SYNC_RUN_NOT_HELD',
    // 409. Approve: the directory now would deactivate someone the held run did not list.
    'DIRECTORY_SYNC_HELD_RUN_STALE',
    // 409. Turning off sync for a person no directory has ever linked (no externalId).
    'DIRECTORY_SYNC_PERSON_NOT_SYNCED',
    // 409. The directory refused the stored grant (revoked, the authorizing admin left, or the stored
    // credential is unreadable). The connection is NEEDS_RECONNECT until an admin reconnects it.
    'DIRECTORY_SOURCE_AUTH_FAILED',
    // 400. An admin-consent return named a directory organization whose verified domains do not include the connecting admin's email domain (S49-3u50 Phase 3).
    'DIRECTORY_SYNC_TENANT_UNVERIFIED',
    // S49-3u50 Phase 4a — SCIM 2.0 (RFC 7644). Answered in the SCIM error envelope on /api/public/v1/scim/v2 only.
    // 400. A `filter` other than `userName eq "…"` or `id eq "…"` (scimType invalidFilter).
    'SCIM_INVALID_FILTER',
    // 400. A PATCH operation this server does not apply: an unknown `op`, a `remove`, or a filtered/URN path (scimType invalidPath).
    'SCIM_INVALID_PATCH',
    // 409. The userName is already provisioned, or the email belongs to someone else here (scimType uniqueness).
    'SCIM_UNIQUENESS',
    // S50-kqp9 central sign-in handoff.
    // 401. POST /auth/handoff with no live central session.
    'SIGN_IN_REQUIRED',
    // 400. Unknown, expired, already-used, wrong-client or wrong-verifier code (one answer, S8).
    'SIGN_IN_HANDOFF_INVALID',
    // 403. The exchange's Origin header is not the named client's origin.
    'SIGN_IN_CLIENT_ORIGIN_MISMATCH',
    // S50-kqp9 Phase 2a — federated sign-in (ADR 0039).
    // 404. Unknown slug or a provider this deployment has not switched on.
    'FEDERATED_PROVIDER_DISABLED',
    // 400. Forged, expired or malformed state, or the browser nonce does not match.
    'FEDERATED_STATE_INVALID',
    // 502. The provider refused the code or answered an error.
    'FEDERATED_PROVIDER_ERROR',
    // 403. The provider did not verify the email address.
    'FEDERATED_EMAIL_UNVERIFIED',
    // 403. No account matches the provider email.
    'FEDERATED_NO_ACCOUNT',
    // 400. Several organizations and none named.
    'FEDERATED_TENANT_REQUIRED',
    // 403. Platform operators cannot sign in this way.
    'FEDERATED_ACCOUNT_NOT_ELIGIBLE',
    // 403. A pending invitation exists.
    'FEDERATED_INVITE_PENDING',
    // 403. The account is suspended.
    'FEDERATED_ACCOUNT_SUSPENDED',
    // 409. The provider identity is linked to another login, or this login already links a different account of that provider.
    'FEDERATED_IDENTITY_CONFLICT',
    // 403. The provider email differs from the invited address.
    'FEDERATED_INVITE_EMAIL_MISMATCH',
    // 409. Removing the only way to sign in.
    'FEDERATED_LAST_SIGN_IN_METHOD',
    // 404. That provider is not linked.
    'FEDERATED_METHOD_NOT_LINKED',
    // S50-kqp9 Phase 2b — sign up with a provider (ADR 0039 decision 9).
    // 410. The pending provider sign-up is missing, used or past its 15 minutes.
    'FEDERATED_SIGNUP_EXPIRED',
    // S50-kqp9 Phase 2c — real Google through Cognito (ADR 0039 decision 10).
    // 409. The provider identity was just bound to the account; start the provider sign-in once more to finish.
    'FEDERATED_SIGN_IN_RESTART',
    // S50-kqp9 Phase 3 — Continue with Microsoft (ADR 0040).
    // 409. A personal Microsoft account matches an existing login; a code went to that login's email. Confirm it to link.
    'FEDERATED_EMAIL_CONFIRMATION_REQUIRED',
    // 403. A Microsoft work or school account whose organization has not set up company sign-in.
    'FEDERATED_WORK_ACCOUNT_UNSUPPORTED',
    // 400. This provider cannot be used to create an account.
    'FEDERATED_SIGNUP_UNSUPPORTED',
    // S50-kqp9 Phase 4a — company sign-in (ADR 0042).
    // 403. The tenant enforces Google company sign-in; a password or another identity was used
    'SSO_GOOGLE_REQUIRED',
    // 403. The same for Microsoft
    'SSO_MICROSOFT_REQUIRED',
    // 404. No company sign-in exists for the caller's tenant
    'SSO_NOT_CONFIGURED',
    // 404. Company sign-in with this provider is not available yet
    'SSO_PROVIDER_UNAVAILABLE',
    // 409. The tenant's company sign-in uses another provider
    'SSO_PROVIDER_MISMATCH',
    // 409. Another tenant already claimed this domain
    'SSO_DOMAIN_CLAIMED',
    // 403. The test sign-in used a personal account
    'SSO_TEST_PERSONAL_ACCOUNT',
    // 403. The test identity is not the caller's own login
    'SSO_TEST_ACCOUNT_MISMATCH',
    // 403. The test identity's company domain is not the claimed domain
    'SSO_TEST_DOMAIN_MISMATCH',
    // 409. No test sign-in by the caller in the last 15 minutes
    'SSO_TEST_REQUIRED',
    // 409. No claimed domain is verified yet
    'SSO_DOMAIN_NOT_VERIFIED',
    // 400. The typed confirmation is not a verified domain
    'SSO_CONFIRMATION_MISMATCH',
    // 409. Company sign-in must be turned off before it is removed
    'SSO_ENFORCED',
    // 409. The TXT record is not published (yet)
    'SSO_DNS_RECORD_NOT_FOUND',
    // 503. DNS cannot be checked (no verifier, or the lookup failed)
    'SSO_DNS_UNAVAILABLE',
    // 409. Sign-up on a domain an organization has claimed and verified
    'SSO_SIGNUP_DOMAIN_CLAIMED',
    // S50-kqp9 Phase 4b — the hourly re-check and the support override (ADR 0042 decisions 12, 14).
    // 401. Company sign-in must re-check this person at their provider
    'SSO_REAUTH_REQUIRED',
    // 409. Company sign-in is not on for everyone, so there is nothing to pause
    'SSO_NOT_ENFORCED',
    // 409. Company sign-in is not paused
    'SSO_NOT_SUSPENDED',
    // S50-kqp9 Phase 4c — Microsoft 365 company sign-in (ADR 0042 decisions 18–19).
    // 409. The tenant already has company sign-in set up
    'SSO_ALREADY_SET_UP',
    // 403. The Microsoft 365 admin did not approve InstaCheckin for the organization
    'SSO_CONSENT_DECLINED',
    // 503. The organization's approval cannot be checked (switched off, or Microsoft did not answer)
    'SSO_CONSENT_UNAVAILABLE',
    // 503. The company sign-in connection could not be created or reached
    'SSO_COMPANY_IDP_UNAVAILABLE',
    // S50-kqp9 Phase 6 — Okta / SAML / OIDC (ADR 0042 decisions 35–50).
    // 403. The tenant enforces its own identity provider (Okta, SAML or OIDC); a password or another identity was used
    'SSO_ENTERPRISE_REQUIRED',
    // 400. A personal email domain cannot be claimed for company sign-in.
    'SSO_DOMAIN_NOT_ALLOWED',
    // 409. The tenant already claims the maximum number of enterprise domains.
    'SSO_DOMAIN_LIMIT_REACHED',
    // 409. The action needs an identity provider that has not been set up yet.
    'SSO_IDP_NOT_CONFIGURED',
    // 400. The SAML metadata could not be parsed or used.
    'SSO_IDP_METADATA_INVALID',
    // 400. Every signing certificate in the SAML metadata has expired.
    'SSO_IDP_CERTIFICATE_EXPIRED',
    // 503. The identity provider could not be reached.
    'SSO_IDP_UNREACHABLE',
    // 400. The OIDC issuer does not publish a usable discovery document.
    'SSO_OIDC_ISSUER_INVALID',
    // 403. The signed-in identity belongs to a different tenant.
    'SSO_ACCOUNT_IN_OTHER_TENANT',
    // 403. Support has not turned on verify-by-email for this domain (or it ended)
    'SSO_EMAIL_FALLBACK_NOT_ENABLED',
    // 409. Verify-by-email is not available for this domain (already verified, or a Microsoft 365 organization)
    'SSO_EMAIL_FALLBACK_UNAVAILABLE',
    // Issue #83. 409. The person's SMS opt-out came from the carrier (a STOP reply) or from the
    // person themselves, so an administrator cannot turn their texts back on.
    'SMS_OPT_OUT_LOCKED',
    // Handoff 0017 §2. 400. A free-form notification address whose domain is not on the tenant's
    // allowed email domains (Settings → Allowed email domains).
    'EMAIL_DOMAIN_NOT_ALLOWED',
    // 400. A consumer mailbox domain (gmail.com, outlook.com, …) can never be an allowed domain.
    'EMAIL_DOMAIN_FREE_MAIL',
    // 409. The domain is already on the tenant's list.
    'EMAIL_DOMAIN_ALREADY_ADDED',
    // 409. This person or address is already on this location's notification list.
    'NOTIFICATION_RECIPIENT_DUPLICATE',
    // 400. The person is not an active employee or contractor of this organization.
    'NOTIFICATION_RECIPIENT_PERSON_UNAVAILABLE',
    // 400. The edit leaves no channel or no trigger on, or turns on texts for an email address.
    'NOTIFICATION_RECIPIENT_INVALID_CHANNELS',
];
/** `TENANT_NOT_FOUND` -> `tenantNotFound`, the i18n key form. */
export function toErrorKey(code) {
    return code
        .toLowerCase()
        .split('_')
        .map((segment, index) => index === 0 ? segment : segment.charAt(0).toUpperCase() + segment.slice(1))
        .join('');
}
export function isErrorCode(value) {
    return ERROR_CODES.includes(value);
}
//# sourceMappingURL=errors.js.map